import React, { useEffect, useState } from 'react';
import {
  Button,
  TextField,
  InputAdornment,
  Typography,
  IconButton,
  Menu,
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { Formik } from 'formik';
import { useHistory, Link } from 'react-router-dom';
import { css } from '@emotion/core';
import { Observer } from 'mobx-react';
import { useRootStore } from '../../stores/BridgeProvider';
import Spinner from '../util/Spinner';
import {
  StyledForm,
  ButtonFlex,
  ErrorText,
  FormRow,
  RowItem,
} from '../styles/styles';
import {
  safeFalsy,
  safeEval,
  formInitialPriceHandler,
} from '../util/UtilFunctions';
import { VoucherTransactionSchema } from '../../validation';
import AccountSelectAC from '../util/AccountSelectAC';
import ACDropdown from '../util/ACDropdown';
import { useSnackBars } from '../../alerts/SnackBarProvider';
import { COLORS } from '../util/Colors';
import { VoucherStatusEnums } from '../common/Enums';

const errorSpan = css`
  font-size: 0.85rem;
  margin-top: 10px;
  color: ${COLORS.red};
`;

const VoucherTransactionForm = ({
  onSubmit,
  voucherTran = {},
  voucherId,
  passedVoucher,
  menuItems,
  buttons,
  submitLabel = 'Submit',
}) => {
  const { dataStore, authStore } = useRootStore();
  const history = useHistory();
  const [isLoading, setLoading] = useState(true);
  const [voucher, setVoucher] = useState(passedVoucher || {});
  const accounts = dataStore.validAccountArray;
  const transactionCategories = dataStore.validTransactionCategoryArray;
  const [anchorEl, setAnchorEl] = useState(null);
  const handleAlert = useSnackBars();
  const open = Boolean(anchorEl);
  const handleClick = e => {
    e.stopPropagation;
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    const initialize = async () => {
      const fetchVoucher = async () => {
        const realVoucherId = voucherTran.VoucherId || voucherId;
        if (!realVoucherId || realVoucherId.toString().includes('tmp-')) return;
        try {
          const { data } = await authStore.request.get(
            `${process.env.REACT_APP_API}/api/voucher/${realVoucherId}`,
          );
          setVoucher(data);
        } catch (e) {
          console.error(e);
        }
      };
      const requests = [];
      if (!passedVoucher) requests.push(fetchVoucher());
      if (accounts.length === 0) {
        requests.push(dataStore.retrieveAccounts());
      }
      if (transactionCategories.length === 0) {
        requests.push(dataStore.retrieveTransactionCategories());
      }
      await Promise.all(requests);
      setLoading(false);
    };
    initialize();
  }, []);

  if (isLoading) return <Spinner />;

  const invalidEdits = edits => {
    // if the voucher is being edited and not created
    if (voucher && voucherTran) {
      // if the voucher had a status of approved or higher
      if (voucher.StatusId && voucher.StatusId > 2) {
        // if user is trying to edit Price of a VT on approved voucher
        if (voucherTran.Price !== edits.Price) {
          // RAISE ERROR
          handleAlert(
            'Cannot edit price on invoice of status "Approved" or higher',
            COLORS.red,
          );
          return true;
        }
      }
    }

    return false;
  };

  return (
    <Observer
      render={() => {
        return (
          <Formik
            validationSchema={VoucherTransactionSchema}
            initialValues={{
              // TODO: Change (voucherTransactionId) on server to match
              VoucherTransactionId: voucherTran.VoucherTranId,
              VoucherId: voucherTran.VoucherId || voucherId,
              CategoryId: safeFalsy(voucherTran.CategoryId),
              AccountId: safeFalsy(voucherTran.AccountId),
              Account: dataStore.getAccountFromId(voucherTran.AccountId),
              Price: formInitialPriceHandler(voucherTran.Price),
              Quantity: voucherTran.Quantity
                ? safeEval(() => parseInt(voucherTran.Quantity, 10), 1)
                : 1,
              Description: safeFalsy(voucherTran.Description),
            }}
            onSubmit={async (values, actions) => {
              try {
                if (invalidEdits(values)) {
                  actions.setSubmitting(false);
                  return;
                }
                await onSubmit(values);
              } catch (e) {
                console.error(e);
              }
              actions.setSubmitting(false);
            }}
            render={({
              handleBlur,
              handleChange,
              errors,
              handleSubmit,
              values,
              isSubmitting,
              setFieldValue,
              dirty,
              touched,
            }) => {
              const showError = val => errors[val] && touched[val];
              const { VoucherTypeDesc = null, VoucherTypeId = null } = voucher;

              const isValidAccountGroup = TypeId => {
                if ([1, 2, 3, 4].includes(VoucherTypeId)) {
                  return ![1, 2].includes(TypeId);
                }
                return true;
              };
              const moveAccountTypeToFront = (list, typeId) => {
                const sorter = (a, b, arr) => {
                  if (arr.includes(a.TypeId)) {
                    return -1;
                  }
                  if (arr.includes(b.TypeId)) {
                    return 1;
                  }
                  return a.TypeId - b.TypeId;
                };

                return list.sort((a, b) => sorter(a, b, typeId));
              };

              const notEditable = voucher.StatusId && voucher.StatusId > 2;

              return (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Typography variant="h2" style={{ margin: '0 auto' }}>
                      {voucherTran.VoucherTranId
                        ? 'Edit Voucher Transaction'
                        : 'New Voucher Transaction'}
                    </Typography>
                    {menuItems && (
                      <div>
                        <IconButton size="small" onClick={handleClick}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                        <Menu
                          anchorEl={anchorEl}
                          open={open}
                          onClose={handleClose}
                        >
                          {menuItems.map(item => item)}
                        </Menu>
                      </div>
                    )}
                  </div>
                  <StyledForm onSubmit={handleSubmit}>
                    {notEditable ? (
                      <span css={errorSpan}>
                        Editing is currently disabled because voucher status is{' '}
                        {VoucherStatusEnums[voucher.StatusId]}
                      </span>
                    ) : null}
                    <FormRow>
                      <RowItem>
                        <ACDropdown
                          disabled={notEditable}
                          textFieldProps={{ margin: 'normal' }}
                          label={
                            <div>
                              Transaction Category{' '}
                              <span style={{ color: 'red' }}>*</span>
                            </div>
                          }
                          options={transactionCategories}
                          onChange={async (e, newVal) => {
                            if (newVal !== null)
                              try {
                                const {
                                  APContraAccountId,
                                  ARContraAccountId,
                                  Name,
                                  Code,
                                } = await dataStore.getTranCategoryFromId(
                                  newVal.TranCategoryId,
                                );

                                if (VoucherTypeDesc === 'Receivable') {
                                  const recAccount = dataStore.getAccountFromId(
                                    ARContraAccountId,
                                  );
                                  setFieldValue('AccountId', ARContraAccountId);
                                  setFieldValue('Account', recAccount);
                                }
                                if (VoucherTypeDesc === 'Payable') {
                                  const payAccount = dataStore.getAccountFromId(
                                    APContraAccountId,
                                  );

                                  dataStore.getAccountFromId(APContraAccountId);
                                  setFieldValue('Account', payAccount);
                                  setFieldValue('AccountId', APContraAccountId);
                                }

                                setFieldValue(
                                  'CategoryId',
                                  newVal.TranCategoryId,
                                );

                                setFieldValue('Description', Name || Code);
                              } catch (error) {
                                console.error(error);
                              }
                          }}
                          value={
                            transactionCategories.find(
                              x => x.TranCategoryId === values.CategoryId,
                            ) || null
                          }
                          getOptionLabel={value => value.Code || null}
                          getOptionSelected={(option, value) => {
                            if (value === '') {
                              return true;
                            }
                            if (
                              option.TranCategoryId === value.TranCategoryId
                            ) {
                              return true;
                            }
                          }}
                        />
                      </RowItem>
                    </FormRow>
                    {showError('CategoryId') && (
                      <ErrorText style={{ textAlign: 'right' }}>
                        {errors.CategoryId}
                      </ErrorText>
                    )}

                    <div style={{ width: '100%', zIndex: 1001 }}>
                      <AccountSelectAC
                        disabled={notEditable}
                        textFieldProps={{ margin: 'normal' }}
                        label={
                          <div>
                            Account <span style={{ color: 'red' }}>*</span>
                          </div>
                        }
                        value={values.Account || null}
                        isValidAccountGroup={isValidAccountGroup}
                        sortFunction={list => {
                          if ([2, 4].includes(VoucherTypeId)) {
                            return moveAccountTypeToFront(list, [7]);
                          }
                          if ([1, 3].includes(VoucherTypeId)) {
                            return moveAccountTypeToFront(list, [6]);
                          }
                          return list;
                        }}
                        onChange={(e, val) => {
                          setFieldValue('Account', val);
                          setFieldValue(
                            'AccountId',
                            safeEval(() => val.AccountId),
                            null,
                          );
                        }}
                      />
                    </div>
                    {showError('AccountId') && (
                      <ErrorText>{errors.AccountId}</ErrorText>
                    )}
                    <FormRow>
                      <TextField
                        disabled={notEditable}
                        name="Price"
                        label="Price *"
                        type="number"
                        value={values.Price}
                        margin="normal"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        variant="outlined"
                        fullWidth
                        error={showError('Price')}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                        }}
                      />
                      <TextField
                        disabled={notEditable}
                        name="Quantity"
                        label="Quantity *"
                        value={values.Quantity}
                        margin="normal"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        variant="outlined"
                        type="number"
                        min="0"
                        step="1"
                        pattern="\d*"
                        fullWidth
                        error={showError('Quantity')}
                      />
                    </FormRow>
                    <TextField
                      disabled={notEditable}
                      label="Description"
                      name="Description"
                      margin="normal"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.Description}
                      variant="outlined"
                      fullWidth
                      error={showError('Description')}
                    />
                    <ButtonFlex>
                      <Button
                        color="primary"
                        variant="contained"
                        type="submit"
                        size="large"
                        disabled={!dirty || isSubmitting}
                        style={{
                          margin: '5px',
                        }}
                      >
                        {submitLabel}
                      </Button>
                      {buttons && buttons.map(button => button)}
                    </ButtonFlex>
                  </StyledForm>
                </div>
              );
            }}
          />
        );
      }}
    />
  );
};

export default VoucherTransactionForm;
